import {
  Incubation,
  Measurement,
  Procedure,
} from "@/services/backend/procedures/procedure/procedure";
import {
  AddMeasurementRequest,
  useAddMeasurementMutation,
} from "@/services/backend/procedures/procedure/service";
import { EvaluableType } from "@/services/backend/sample-error/sample-error";
import { GrowthMedium } from "@/services/backend/types";
import { hasFieldError } from "@/shared/app-lib/errors/validation-error";
import { useAuth } from "@/shared/lib/authorization/auth-context";
import { User } from "@/shared/lib/authorization/user";
import { useDebouncedMutationWithPersistenceStateContextUpdate } from "@/shared/lib/debounce/debounce";
import { useErrArtefactNotReady } from "@/shared/service-manager/artefact/err-artefact-not-ready";
import { ChangeEvent, ReactElement, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { Checkbox } from "../../ui/checkbox";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";

const AUTOSAVE_DEBOUNCE_MS = 1500;

const getGrowthMediaLabel = (incubation: Incubation): string => {
  if (incubation.sequenceNumber === 1) {
    if (incubation.growthMedium === GrowthMedium.Caso) {
      return "Bakterien";
    }
  }

  if (incubation.sequenceNumber === 2) {
    if (incubation.growthMedium === GrowthMedium.Caso) {
      return "Schimmelpilze";
    }
    return "Schimmelpilze / Hefen";
  }

  return "";
};

function getMeasurementFromIncubation(
  incubation: Incubation,
  user: User,
): Measurement {
  const previousMeasurement = incubation.measurements.find(
    (currentMeasurement) => currentMeasurement.isCurrent,
  );

  return previousMeasurement
    ? { ...previousMeasurement, id: uuidv4(), employeeId: user.id as string }
    : {
        id: uuidv4(),
        employeeId: user?.id as string,
        incubationId: incubation.id,
        isCurrent: false,
        exceedsThreshold: false,
        measurementDate: "",
        measurement: undefined,
        correctedMeasurement: undefined,
      };
}

function getInitialInput(measurement: Measurement | undefined): string {
  if (measurement != null) {
    if (measurement.measurement === 0) {
      return "0";
    }

    if (measurement.measurement == null) {
      return "";
    }

    return measurement.measurement.toString();
  }

  return "";
}

interface MeasurementInputComponentProps {
  incubation: Incubation;
  procedure: Procedure;
  disabled?: boolean;
}

function toAddMeasurementRequest(
  measure: Measurement,
  procedureId: string,
): AddMeasurementRequest {
  return {
    id: measure.id,
    employeeId: measure.employeeId,
    exceedsThreshold: measure.exceedsThreshold,
    incubationId: measure.incubationId,
    value: measure.measurement,
    measurementTime: new Date().toISOString(),
    procedureId,
  };
}

export default function MeasurementInputComponent({
  incubation,
  procedure,
  disabled = false,
}: MeasurementInputComponentProps): ReactElement {
  const { user } = useAuth();
  const [measurement, setMeasurement] = useState<Measurement>(
    getMeasurementFromIncubation(incubation, user!),
  );
  const [editingValue, setEditingValue] = useState(
    getInitialInput(measurement),
  );

  const { notReadyError } = useErrArtefactNotReady();
  const hasMeasurementError = hasFieldError(
    notReadyError,
    `incubation.${incubation.id}.measurement-${incubation.sequenceNumber}`,
  );

  const [request, setRequest] = useState<AddMeasurementRequest>(() =>
    toAddMeasurementRequest(measurement, procedure.id),
  );
  const [addMeasurement, { isLoading, error, isSuccess, reset }] =
    useAddMeasurementMutation();
  useDebouncedMutationWithPersistenceStateContextUpdate(
    request,
    addMeasurement,
    isLoading,
    error,
    isSuccess,
    reset,
    AUTOSAVE_DEBOUNCE_MS,
    { toastError: true },
  );

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const id = uuidv4();
    let newMeasurement;

    if (inputValue === "") {
      newMeasurement = {
        ...measurement,
        id,
        measurement: undefined,
        exceedsThreshold: false,
      };
    } else if (!Number.isNaN(Number(inputValue))) {
      const numericValue = Number(inputValue);

      if (numericValue > 100) {
        newMeasurement = {
          ...measurement,
          id,
          measurement: undefined,
          exceedsThreshold: true,
        };
      } else {
        newMeasurement = {
          ...measurement,
          id,
          measurement: numericValue,
          exceedsThreshold: false,
        };
      }
    } else {
      newMeasurement = {
        ...measurement,
        id,
        measurement: undefined,
        exceedsThreshold: false,
      };
    }

    setMeasurement(newMeasurement);
    setRequest(toAddMeasurementRequest(newMeasurement, procedure.id));
    setEditingValue(getInitialInput(newMeasurement));
  };

  const handleCheckboxChange = (checked: boolean) => {
    const newMeasurement = {
      ...measurement,
      id: uuidv4(),
      measurement: undefined,
      exceedsThreshold: checked,
    };
    setMeasurement(newMeasurement);
    setRequest(toAddMeasurementRequest(newMeasurement, procedure.id));
    setEditingValue(getInitialInput(newMeasurement));
  };

  return (
    <div className="w-full max-w-48 space-y-2">
      <div
        className={
          hasMeasurementError ? "rounded-lg border border-red-500 p-1" : ""
        }
      >
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <Input
              id={`measurement-${measurement.id}`}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="(0-100)"
              value={editingValue}
              onChange={handleInputChange}
              className="w-full"
              disabled={
                measurement.exceedsThreshold ||
                incubation.evaluableType === EvaluableType.NotEvaluable ||
                disabled
              }
            />
          </div>
          <div className="flex items-center space-x-1">
            <Checkbox
              id={`checkbox-${measurement.id}`}
              checked={measurement.exceedsThreshold}
              onCheckedChange={handleCheckboxChange}
              disabled={
                incubation.evaluableType === EvaluableType.NotEvaluable ||
                disabled
              }
            />
            <Label
              htmlFor={`checkbox-${measurement.id}`}
              className="text-sm font-medium"
            >
              {"> 100"}
            </Label>
          </div>
        </div>
      </div>
      <div className="flex flex-col space-y-1">
        <p className="text-muted-foreground">
          {getGrowthMediaLabel(incubation)}
        </p>
      </div>
    </div>
  );
}
